import React, { memo, useCallback, useContext, useMemo } from 'react';
import RaffleEventSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/RaffleEventSection';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/contexts/ModalsContext';
import NoticeModal from '@wconcept/wconcept-ui-renewal/components/organisms/_modal/NoticeModal';
import RaffleEventModal from '@wconcept/wconcept-ui-renewal/components/organisms/_modal/RaffleEventModal';
import { PromotionEntryModule } from '@wconcept/wconcept-common/models/renewal/promotion';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { isInDisplayTimeRange } from '@components/pages/promotion/utils/displayTimeChecker';
import { addAsIsMWebDomain, currentUrl } from '@utils/domainUtil';
import useGlobalState from '@hooks/useGlobalState';
import useGa4Event from '@hooks/useGa4Event';
import useModalManager from '@hooks/useModalManager';
import {
  getCookieWckAuthData,
  getCookieWckMktAdValue,
  setCookieWckMKtAdValue,
} from '@wconcept/wconcept-common/utils/cookie';
import { isEmpty } from 'lodash-es';
import useHistoryRouter from '@hooks/useLinkTo';
import {
  requestPromotionEntry,
  requestGetPromotionEntryApplied,
} from '@apis/renewal/promotion';
import dayjs from 'dayjs';
import { useToastPopupStore } from '@store/toast';

interface IProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  module: PromotionEntryModule;
  promotionId: number;
  promotionTitle?: string;
}

const ACTION_BAR_SETTLE_DELAY_MS = 300;

const openModalAfterActionBarSettle = (
  willResizeActionBar: boolean,
  openFn: () => void
): void => {
  if (!willResizeActionBar) {
    openFn();
    return;
  }
  setTimeout(openFn, ACTION_BAR_SETTLE_DELAY_MS);
};

/** 응모 참여 API 실패 시 서버 메시지가 없을 때 노출할 기본 문구 */
const ENTRY_ERROR_FALLBACK_MESSAGE =
  '이벤트 참여에 실패했습니다.<br/>잠시 후 다시 시도해 주세요.';

const getEntryErrorMessage = (serverMessage?: string): string =>
  serverMessage?.trim() || ENTRY_ERROR_FALLBACK_MESSAGE;

function EntryModule({
  bgType,
  visualTokens,
  isDesktop,
  module,
  promotionId,
  promotionTitle,
}: IProps) {
  const { openModal } = useContext(ModalsContext);
  const { assistData, isMobile, appInfo } = useGlobalState();
  const { openLoginConfirm } = useModalManager();
  const { linkTo } = useHistoryRouter();
  const { sendGa4ButtonClick } = useGa4Event();

  // 응모(RAFFLE)=engage / 알림(ALARM)=alert
  const gaBannerType = module.entryType === 'ALARM' ? 'alert' : 'engage';

  /** button_click 공통 파라미터 (issueCD/NM, variable, bannerType) */
  const sendEntryButtonClick = useCallback(
    (area: string, text?: string) => {
      sendGa4ButtonClick({
        area,
        text,
        page: 'promotion',
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        variable: module.title,
        bannerType: gaBannerType,
      });
    },
    [
      sendGa4ButtonClick,
      promotionId,
      promotionTitle,
      module.title,
      gaBannerType,
    ]
  );
  const setToastMessage = useToastPopupStore(state => state.setToastMessage);

  const getIsGnbType = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('gnbType') === 'Y';
  }, []);

  const getUserNo = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.userNo;
  }, []);
  const getUserId = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.userId;
  }, []);

  const getMobile = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.mobile;
  }, []);

  const getAdYN = useCallback(async () => {
    const auth = await getCookieWckAuthData();
    return auth.adYN;
  }, []);

  const isNativeApp = useCallback(
    () => !!appInfo?.isNativeAppBoth,
    [appInfo?.isNativeAppBoth]
  );

  /** 앱(gnbType=Y) 진입 시 끈 액션바 복구 */
  const restoreActionBar = useCallback(() => {
    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
      document.location.href = 'wconceptkr://actionBarOn';
    }
  }, [appInfo?.isNativeAppBoth, getIsGnbType]);

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });
  const sectionColorType = customColorsFull ?? presetBgType;
  const buttonText = module.entryBtnName?.trim() || '이벤트 참여하기';

  const noticeList = useMemo(
    () =>
      (module.notice ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0),
    [module.notice]
  );

  const thirdPartyConentList = useMemo(
    () =>
      (module.thirdPartyProvisionContent ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0),
    [module.thirdPartyProvisionContent]
  );

  const imgSrc = isDesktop ? module.pcImageUrl : module.moImageUrl;

  const handleEntryClick = useCallback(async () => {
    // GA: 참여(신청) 버튼 클릭
    sendEntryButtonClick('promotion_engage_button');
    // 본인 인증 프로세스 여부
    const { authNecessaryYn } = module;
    // 제 3자 제공 프로세스 여부
    const { thirdPartyProvisionYn } = module;
    // 마케팅 수신 동의 프로세스 여부
    const { marketingConsentYn } = module;
    // 로그인 상태
    const isLoggedIn = !!(
      assistData?.isLogin && assistData?.user?.userNo !== '0'
    );
    // 쿠폰 발급 여부
    const isCouponIssued = module.rewardType === 'COUPON';
    // 본인 인증 상태
    const isAuthenticated = !isEmpty(await getMobile());
    // 마케팅 수신 동의 상태
    const mktAdValueYn = await getCookieWckMktAdValue();
    const isMarketingAgreed = mktAdValueYn === 'Y' || (await getAdYN()) === 'Y';
    // 마케팅 수신 동의 타입
    const { marketingConsentType } = module;

    const modalType = isDesktop ? 'modal' : 'drawer';

    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
      document.location.href = 'wconceptkr://actionBarOff';
    }

    // 1. 로그인 확인
    if (!isLoggedIn) {
      restoreActionBar();
      await openLoginConfirm();
      return;
    }

    // 2. 본인인증 확인
    if (authNecessaryYn === 'Y' && !isAuthenticated) {
      openModal(NoticeModal, {
        type: modalType,
        onClose: () => {
          if (appInfo?.isNativeAppBoth && getIsGnbType()) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
        },
        onSubmit: async () => {
          if (appInfo?.isNativeAppBoth && getIsGnbType()) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
          const targetUrl = `${await addAsIsMWebDomain(
            !isMobile ? '/MyPage/MemberInfo' : '/Member/SelfCertification'
          )}?reqFrom=ISSUE_LIVE&returnUrl=${encodeURIComponent(
            currentUrl({ isAddTimestamp: true })
          )}`;
          if (appInfo?.isNativeAppBoth && getIsGnbType() && isMobile) {
            document.location.href = `wconceptkr://newWebviewOpen?url=${encodeURIComponent(
              targetUrl
            )}`;
            return;
          }
          linkTo.push({
            url: targetUrl,
          });
        },
        value: {
          text: '본인인증이 필요합니다.<br/>마이페이지로 이동하여<br/> <b>본인인증</b>을 진행하시겠습니까?',
          btnType: 'double',
          closeText: '취소',
          submitText: '확인',
          isIPhoneXApp: appInfo.isIPhoneXApp, // 앱체크
          isDesktop: !isMobile,
        },
      });
      return;
    }

    // 3. 이벤트 기간 확인
    const inPeriod = isInDisplayTimeRange(
      module.entryStartDate,
      module.entryEndDate
    );
    if (!inPeriod) {
      openModal(NoticeModal, {
        type: modalType,
        value: {
          text: '이벤트 기간이 아닙니다.',
          btnType: 'single',
          submitText: '확인',
          isDesktop,
          isIPhoneXApp: appInfo.isIPhoneXApp,
        },
        onSubmit: restoreActionBar,
        onClose: restoreActionBar,
      });
      return;
    }

    // 4. 참여 이력 확인
    try {
      const isApplied = await requestGetPromotionEntryApplied(
        promotionId,
        module.moduleId
      );
      if (isApplied?.applied === true) {
        openModal(NoticeModal, {
          type: modalType,
          value: {
            text: '이미 참여한 이벤트입니다.',
            btnType: 'single',
            submitText: '확인',
            isDesktop,
            isIPhoneXApp: appInfo.isIPhoneXApp,
          },
          onSubmit: restoreActionBar,
          onClose: restoreActionBar,
        });
        return;
      }
    } catch (error) {
      // 참여이력 조회 실패 시 서버 메시지(없으면 기본 문구)로 차단 안내
      const apiError = error as { message?: string };
      openModal(NoticeModal, {
        type: modalType,
        value: {
          text: getEntryErrorMessage(apiError?.message),
          btnType: 'single',
          submitText: '확인',
          isDesktop,
          isIPhoneXApp: appInfo.isIPhoneXApp,
        },
        onSubmit: restoreActionBar,
        onClose: restoreActionBar,
      });
      return;
    }

    // 5. 개인정보 제3자 제공 동의 모달
    // 액션바 리사이즈(actionBarOff)와 슬라이드업이 겹치지 않도록 지연 오픈
    // (actionBarOff 호출 조건과 동일: 앱 + gnbType=Y 일 때만 리사이즈 발생)
    const willResizeActionBar = !!appInfo?.isNativeAppBoth && getIsGnbType();
    openModalAfterActionBarSettle(willResizeActionBar, () => {
      openModal(RaffleEventModal, {
        type: modalType,
        value: {
          thirdPartyProvisionYn,
          marketingConsentYn,
          isMarketingAgreed,
          thirdPartyConentList,
          marketingConsentType,
          isIPhoneXApp: appInfo.isIPhoneXApp,
          isDesktop,
          isNativeApp: !!appInfo?.isNativeAppBoth,
        },
        onClose: restoreActionBar,
        onSubmit: async () => {
          // GA: 모달 '동의하고 참여하기' 버튼 클릭
          sendEntryButtonClick(
            'promotion_engage_confirm_button',
            '동의하고 참여하기'
          );
          /** 서버 메시지(없으면 기본 문구)로 안내 모달 노출 */
          const showEntryError = (serverMessage?: string) => {
            openModal(NoticeModal, {
              type: modalType,
              value: {
                text: getEntryErrorMessage(serverMessage),
                btnType: 'single',
                submitText: '확인',
                isDesktop,
                isIPhoneXApp: appInfo.isIPhoneXApp,
              },
              onSubmit: restoreActionBar,
              onClose: restoreActionBar,
            });
          };

          try {
            // 참여·동의 적재 API 호출
            const receiveEntryRes = await requestPromotionEntry(
              promotionId,
              module.moduleId
            );
            if (receiveEntryRes?.result === 'SUCCESS') {
              const isMarketingNewlyAgreed =
                marketingConsentYn === 'Y' && !isMarketingAgreed;

              if (isMarketingNewlyAgreed) {
                await setCookieWckMKtAdValue('Y');
                if (isNativeApp() && marketingConsentType === 'PUSH') {
                  (
                    window as {
                      WCKAppFunction?: {
                        setAlarm?: (...args: unknown[]) => void;
                      };
                    }
                  )?.WCKAppFunction?.setAlarm?.(true, 'enableAlarmComplete', {
                    custid: await getUserId(),
                    custno: await getUserNo(),
                  });
                }
              }

              const successText =
                module.entryType === 'ALARM'
                  ? '이벤트 참여가 완료되었습니다.'
                  : '이벤트 참여가 완료되었습니다.<br/>공지사항을 통해 당첨 여부를 확인해 주세요.';

              // 참여 완료 모달
              openModal(NoticeModal, {
                type: modalType,
                value: {
                  text: successText,
                  btnType: 'single',
                  submitText: '확인',
                  isDesktop,
                  isIPhoneXApp: appInfo.isIPhoneXApp,
                },
                onSubmit: () => {
                  restoreActionBar();

                  if (isMarketingNewlyAgreed) {
                    const today = dayjs().format('YYYY년 MM월 DD일');
                    // 모달 채널 노출과 동일 기준: 앱 + PUSH 일 때만 '푸시' 포함
                    const marketingChannelText =
                      isNativeApp() && marketingConsentType === 'PUSH'
                        ? 'SMS, 이메일, 푸시'
                        : 'SMS, 이메일';
                    setToastMessage(
                      `[W컨셉] ${today} 광고성<br />정보 ${marketingChannelText} 수신동의 처리되었습니다.`
                    );
                  }

                  if (isCouponIssued) {
                    if (isMarketingNewlyAgreed) {
                      setTimeout(
                        () => setToastMessage('쿠폰이 발급되었습니다.'),
                        2400
                      );
                    } else {
                      setToastMessage('쿠폰이 발급되었습니다.');
                    }
                  }
                },
                onClose: restoreActionBar,
              });
              return;
            }
            showEntryError(receiveEntryRes?.message);
          } catch (error) {
            const apiError = error as { message?: string };
            showEntryError(apiError?.message);
          }
        },
      });
    });
  }, [
    openModal,
    isDesktop,
    isMobile,
    appInfo,
    module.entryStartDate,
    module.entryEndDate,
    module.authNecessaryYn,
    module.thirdPartyProvisionYn,
    module.marketingConsentYn,
    module.marketingConsentType,
    module.rewardType,
    module.moduleId,
    module.entryType,
    assistData?.isLogin,
    assistData?.user?.userNo,
    openLoginConfirm,
    getMobile,
    getAdYN,
    getUserId,
    getUserNo,
    getIsGnbType,
    isNativeApp,
    linkTo,
    promotionId,
    restoreActionBar,
    thirdPartyConentList,
    setToastMessage,
    sendEntryButtonClick,
  ]);

  // GA: 유의사항 토글 (열림='유의사항 보기' / 닫힘='유의사항 닫기')
  const handleNoticeToggle = useCallback(
    (active: boolean) => {
      sendEntryButtonClick(
        'promotion_engage_notice_button',
        active ? '유의사항 보기' : '유의사항 닫기'
      );
    },
    [sendEntryButtonClick]
  );

  return (
    <RaffleEventSection
      imgSrc={imgSrc}
      colorType={sectionColorType}
      buttonText={buttonText}
      noticeList={noticeList.length > 0 ? noticeList : undefined}
      onClick={handleEntryClick}
      onNoticeToggle={handleNoticeToggle}
    />
  );
}

export default memo(EntryModule);
